import {useCallback} from 'react'
import {isFeatureEnabled} from '@github-ui/feature-flags'
import {useQueryClient} from '@github-ui/react-query'
import {PageData} from '@github-ui/pull-request-page-data-tooling/page-data'
import {usePageDataUrl} from '@github-ui/pull-request-page-data-tooling/use-page-data-url'
import {
  useRespondToAliveUpdate,
  type PullRequestAliveContext,
  type PullRequestAliveData,
} from './use-refetch-on-alive-update'
import {descriptionQueryKey, type DescriptionData} from '../page-data/loaders/use-description-data'
import {useInvalidateOnAliveUpdate} from './use-invalidate-on-alive-update'

export function useDescriptionLiveUpdates(channel: string): void {
  const queryClient = useQueryClient()
  const descriptionURL = usePageDataUrl(PageData.description)
  const dedupeConcurrentRefetches = isFeatureEnabled('merge_status_checks_refetch_dedupe')

  // Original refetch path (flag OFF); empty channel no-ops it when ON.
  const refetchHandler = useCallback(
    (context: PullRequestAliveContext | undefined) => {
      const queryKey = descriptionQueryKey(descriptionURL)
      // If the published body version matches what we already have cached
      // (e.g. because our own mutation primed the cache from the response),
      // there is nothing new to fetch.
      const publishedVersion = context?.description_body_version
      if (publishedVersion) {
        const cached = queryClient.getQueryData<DescriptionData>(queryKey)
        if (cached?.bodyVersion === publishedVersion) return
      }
      queryClient.refetchQueries({queryKey})
    },
    [queryClient, descriptionURL],
  )
  useRespondToAliveUpdate(dedupeConcurrentRefetches ? '' : channel, refetchHandler, {body_updated: true})

  const filter = useCallback(
    (data: PullRequestAliveData) => {
      if (!data.event_updates?.body_updated) return false

      const context = data.event_context
      const queryKey = descriptionQueryKey(descriptionURL)
      const publishedVersion = context?.description_body_version

      if (publishedVersion) {
        const cached = queryClient.getQueryData<DescriptionData>(queryKey)
        if (cached?.bodyVersion === publishedVersion) return false
      }

      return true
    },
    [queryClient, descriptionURL],
  )

  // Additive dedupe path (flag ON); empty channel no-ops it when OFF. Preserves the
  // same body-version reconciliation but invalidates instead of
  // forcing a refetch, joining any in-flight refetch for the key.
  useInvalidateOnAliveUpdate(dedupeConcurrentRefetches ? channel : '', descriptionQueryKey(descriptionURL), {
    filter,
  })
}
